import { type HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { useAppDispatch, useAppSelector } from '@services/state/store';
import React from 'react';
import { aadConfig, getAccessToken } from '../../aad';
import type { Comment } from '../../dto/comment';
import { useCurrentUserAccount, useMe } from '../account';
import { commentsActions } from './slice';
import type { CommentChannel, CommentChannels, CommentPanelState, CommentPanelView, CommentsState } from './types';

let connection: HubConnection | undefined;

export const RECENT_COMMENTS_KEY = '$recent$';

export const useCommentWs = () => {
  const [account] = useCurrentUserAccount();
  const [me] = useMe();

  const latestState = useAppSelector((state) => state.comments);
  const dispatch = useAppDispatch();

  const state = React.useRef<CommentsState>(latestState);
  React.useEffect(() => {
    state.current = latestState;
  }, [latestState]);

  const channelsRef = React.useRef<CommentChannels>(state.current.channels);
  channelsRef.current = state.current.channels;

  const currentChannelRef = React.useRef<CommentChannel>(state.current.currentChannel);
  currentChannelRef.current = state.current.currentChannel;

  const fetchChannels = React.useCallback(async (accountId: string, projectId?: string) => {
    if (connection?.state === HubConnectionState.Connected)
      await connection.send('FetchChannels', accountId, projectId || null);
  }, []);

  const fetchComments = React.useCallback(async (channel: string, sourceChannel: string | null, before: number) => {
    if (connection?.state === HubConnectionState.Connected)
      await connection.send('FetchComments', channel, sourceChannel, before, null);
  }, []);

  const fetchRecent = React.useCallback(async () => {
    if (connection?.state === HubConnectionState.Connected) await connection.send('FetchRecent');
  }, []);

  const postComment = React.useCallback(
    async (content: unknown, plainText: string, url?: string) => {
      if (connection?.state !== HubConnectionState.Connected || !currentChannelRef.current) return;

      const { channel, sourceChannel } = currentChannelRef.current;
      await connection.send('Post', channel, sourceChannel, JSON.stringify(content), plainText, url, null, null);

      // Announced so a screen can wait for the comment before acting on it.
      // Returning a Basis of Estimate for changes needs the reason to exist
      // first: the provider is told the moment the status flips, and being
      // told without being told why is the whole complaint.
      window.dispatchEvent(new CustomEvent('commentPosted', { detail: { channel, sourceChannel, plainText } }));
    },
    [account],
  );

  const handleFetchChannels = React.useCallback((chs: CommentChannels) => {
    dispatch(commentsActions.setChannels(chs));
    dispatch(commentsActions.setCurrentChannel(chs.groups[0]));
  }, []);

  const addComments = React.useCallback((comments: Comment[], isRecent?: boolean) => {
    const newComments = { ...state.current.comments };

    if (isRecent) {
      comments.forEach((c) => {
        if (!newComments[RECENT_COMMENTS_KEY]) newComments[RECENT_COMMENTS_KEY] = [];
        else newComments[RECENT_COMMENTS_KEY] = newComments[RECENT_COMMENTS_KEY].map((x) => ({ ...x }));

        const idx = newComments[RECENT_COMMENTS_KEY].findIndex((x) => x.id === c.id);
        if (idx < 0) newComments[RECENT_COMMENTS_KEY].push(c);
        else newComments[RECENT_COMMENTS_KEY][idx] = c;
      });

      if (!newComments[RECENT_COMMENTS_KEY]) newComments[RECENT_COMMENTS_KEY] = [];
      else newComments[RECENT_COMMENTS_KEY].map((x) => ({ ...x }));

      newComments[RECENT_COMMENTS_KEY] = newComments[RECENT_COMMENTS_KEY].sort((a, b) => a.createdAt - b.createdAt);
    }

    let isInitLoad = false;

    if (!comments.length) {
      if (!currentChannelRef.current) return;

      const currentCh = currentChannelRef.current;
      let isInitLoad = false;

      let key = `${currentCh.channel}${currentCh.sourceChannel || ''}`;
      if (!newComments[key]) {
        newComments[key] = [];
        isInitLoad = true;
      } else {
        newComments[key] = newComments[key].map((x) => ({ ...x }));
      }

      if (currentCh.sourceChannel) {
        key = `${currentCh.sourceChannel}${currentCh.channel}`;
        if (!newComments[key]) {
          newComments[key] = [];
          isInitLoad = true;
        } else {
          newComments[key] = newComments[key].map((x) => ({ ...x }));
        }
      }

      if (isInitLoad) window.dispatchEvent(new Event('initLoadComments'));

      dispatch(commentsActions.setComments(newComments));
      return;
    }

    comments.forEach((c) => {
      let key = `${c.channel}${c.sourceChannel || ''}`;

      if (!newComments[key]) {
        newComments[key] = [];
        isInitLoad = true;
      } else {
        newComments[key] = newComments[key].map((x) => ({ ...x }));
      }

      let idx = newComments[key].findIndex((x) => x.id === c.id);
      if (idx < 0) newComments[key].push(c);
      else newComments[key][idx] = c;

      newComments[key] = newComments[key].sort((a, b) => a.createdAt - b.createdAt);

      if (c.sourceChannel) {
        key = `${c.sourceChannel}${c.channel}`;

        if (!newComments[key]) {
          newComments[key] = [];
          isInitLoad = true;
        } else {
          newComments[key] = newComments[key].map((x) => ({ ...x }));
        }

        idx = newComments[key].findIndex((x) => x.id === c.id);
        if (idx < 0) newComments[key].push(c);
        else newComments[key][idx] = c;

        newComments[key] = newComments[key].sort((a, b) => a.createdAt - b.createdAt);
      }
    });

    if (isInitLoad) window.dispatchEvent(new Event('initLoadComments'));
    dispatch(commentsActions.setComments(newComments));
  }, []);

  const handleFetchComments = React.useCallback(
    (comments: Comment[]) => {
      addComments(comments);
    },
    [state],
  );

  const handleRecentComments = React.useCallback(
    (comments: Comment[]) => {
      addComments(comments, true);
    },
    [addComments],
  );

  const handleNewChannels = React.useCallback(() => {}, [state]);

  const handleNewComment = React.useCallback(
    (comment: Comment) => {
      addComments([comment], true);

      window.dispatchEvent(new Event('newComment'));

      if (comment.sourceUser.id === me?.id) return;

      if (channelsRef.current) {
        const ch = JSON.parse(JSON.stringify(channelsRef.current)) as CommentChannels;

        const chs = comment.sourceChannel
          ? ch.direct.filter(
              (x) =>
                (x.channel === comment.channel && x.sourceChannel === comment.sourceChannel) ||
                (x.channel === comment.sourceChannel && x.sourceChannel === comment.channel),
            )
          : ch.groups.filter((x) => x.channel === comment.channel);

        if (chs.length) {
          chs.forEach((x) => {
            x.newCount = (x.newCount || 0) + 1;
          });
          dispatch(commentsActions.setChannels(ch));
        }
      }
    },
    [addComments, me?.id],
  );

  const handleUpdateChannels = React.useCallback(() => {}, [state]);

  const handleUpdateComment = React.useCallback(
    (comment: Comment) => {
      addComments([comment]);
    },
    [addComments],
  );

  const handleDeleteChannels = React.useCallback(() => {}, [state]);

  const handleDeleteComment = React.useCallback(() => {}, [state]);

  React.useEffect(() => {
    if (!account?.id || connection?.baseUrl.endsWith(account.id)) return;

    const init = async () => {
      if (connection?.state === HubConnectionState.Connected) await connection.stop();

      dispatch(commentsActions.reset());

      // Build the connection
      const url = `${process.env.COMMENT_API}/comments?aid=${account.id}`;
      connection = new HubConnectionBuilder()
        .configureLogging(LogLevel.Warning)
        .withUrl(url, {
          accessTokenFactory: () => getAccessToken(aadConfig().commentScopes).then((r) => r as string),
        })
        .withAutomaticReconnect()
        .build();

      try {
        await connection.start();
        return connection;
      } catch (err) {
        console.error('SignalR Connection Error: ', err);
      }
    };

    init().then((connection) => {
      if (!connection) return;
      connection.on('RecvFetchChannels', handleFetchChannels);
      connection.on('RecvFetchComments', handleFetchComments);
      connection.on('RecvRecentComments', handleRecentComments);
      connection.on('RecvNewChannels', handleNewChannels);
      connection.on('RecvNewComment', handleNewComment);
      connection.on('RecvUpdateChannels', handleUpdateChannels);
      connection.on('RecvUpdateComment', handleUpdateComment);
      connection.on('RecvDeleteChannels', handleDeleteChannels);
      connection.on('RecvDeleteComment', handleDeleteComment);
    });

    return () => {
      if (connection?.state === HubConnectionState.Connected) connection.stop();
    };
  }, [account?.id]);

  React.useEffect(() => {
    if (!state.current.currentChannel || !channelsRef.current) return;

    const ch = JSON.parse(JSON.stringify(channelsRef.current)) as CommentChannels;

    const chs = state.current.currentChannel.sourceChannel
      ? ch.direct.filter(
          (x) =>
            (x.channel === state.current.currentChannel?.channel &&
              x.sourceChannel === state.current.currentChannel.sourceChannel) ||
            (x.channel === state.current.currentChannel?.sourceChannel &&
              x.sourceChannel === state.current.currentChannel.channel),
        )
      : ch.groups.filter((x) => x.channel === state.current.currentChannel?.channel);

    if (chs.length) {
      chs.forEach((x) => {
        x.newCount = 0;
      });
      dispatch(commentsActions.setChannels(ch));
    }
  }, [state.current.currentChannel]);

  return {
    ready: state.current.ready,
    connection: connection,
    fetchChannels,
    fetchComments,
    fetchRecent,
    postComment,
  };
};

export const useCommentPanel = () => {
  const panel = useAppSelector((state) => state.comments.panel);
  const dispatch = useAppDispatch();

  return {
    panel,
    setPanelState: (panel: CommentPanelState) => dispatch(commentsActions.setPanel(panel)),
    setPanelView: (view: CommentPanelView) => dispatch(commentsActions.setPanelView(view)),
  };
};

export const useCommentChannels = (accountId?: string, projectId?: string) => {
  const channels = useAppSelector((state) => state.comments.channels);
  const currentChannel = useAppSelector((state) => state.comments.currentChannel);
  const dispatch = useAppDispatch();
  const { fetchChannels } = useCommentWs();

  const setChannels = React.useCallback((channels?: CommentChannels) => {
    dispatch(commentsActions.setChannels(channels));
    dispatch(commentsActions.setCurrentChannel(channels?.groups[0]));
  }, []);

  React.useEffect(() => {
    if (!accountId) return;

    if (channels?.accountId === accountId && channels?.projectId === projectId) return;
    fetchChannels(accountId, projectId).then(() => {});
  }, [accountId, projectId, fetchChannels]);

  const setChannel = React.useCallback((ch?: CommentChannel) => {
    window.dispatchEvent(new Event('commentChannelChangeStart'));
    dispatch(commentsActions.setCurrentChannel(ch));
    setTimeout(() => window.dispatchEvent(new Event('commentChannelChangeDone')));
  }, []);

  return {
    channels,
    setChannels,
    currentChannel,
    setCurrentChannel: setChannel,
  };
};

export const useComments = () => {
  const state = useAppSelector((state) => state.comments);
  const dispatch = useAppDispatch();

  const { fetchComments, fetchRecent } = useCommentWs();

  const loadComments = React.useCallback(
    (before?: number) => {
      if (!state.currentChannel) return;
      fetchComments(
        state.currentChannel.channel,
        state.currentChannel.sourceChannel ?? null,
        before ?? Date.now(),
      ).then(() => {});
    },
    [state.currentChannel, fetchComments],
  );

  const loadRecent = React.useCallback(() => {
    fetchRecent().then(() => {});
  }, [fetchRecent]);

  const setChannel = React.useCallback((ch?: CommentChannel) => {
    window.dispatchEvent(new Event('commentChannelChangeStart'));
    dispatch(commentsActions.setCurrentChannel(ch));
    setTimeout(() => window.dispatchEvent(new Event('commentChannelChangeDone')));
  }, []);

  return {
    ready: state.ready,
    comments: state.comments,
    loadComments,
    loadRecent,
    currentChannel: state.currentChannel,
    setCurrentChannel: setChannel,
  };
};
